import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import JsBarcode from "jsbarcode";

/**
 * Product- en verpakkingsstickers als PDF.
 *
 * Dit is een port van `components/ProductStickers.tsx` uit het
 * leveranciersportaal (BMX-10). Beide apps printen dezelfde sticker, dus een
 * wijziging aan de opmaak, het formaat of de barcode hoort op allebei de
 * plekken te gebeuren. De enige bewuste verschillen zijn het pad naar het logo
 * (`/img/logo.webp` hier, `/logo.webp` in het portaal) en het feit dat de
 * meldingen hier door i18n gaan.
 *
 * Sticker: 150 × 100 mm liggend, één pagina per sticker.
 */

const INLINE_PADDING = 60;
const STICKER_PRIMARY = "#938370";
const STICKER_FOOTER_BG = "#A7967B";

/**
 * EAN-13: dertien cijfers waarvan de laatste een controlecijfer is.
 *
 * JsBarcode gooit een error bij ongeldige invoer in plaats van hem over te
 * slaan. Een product zonder (geldige) barcode krijgt daarom een sticker zonder
 * barcodeblok plus een waarschuwing — liever geen barcode dan een verkeerde op
 * de doos.
 */
export const normalizeEan13 = (value) => {
  if (!value) return null;
  const digits = String(value).trim();
  if (!/^\d{13}$/.test(digits)) return null;

  const checkDigit = Number(digits[12]);
  const sum = digits
    .slice(0, 12)
    .split("")
    .reduce((acc, digit, index) => acc + Number(digit) * (index % 2 === 0 ? 1 : 3), 0);

  return (10 - (sum % 10)) % 10 === checkDigit ? digits : null;
};

export const isValidEan13 = (value) => normalizeEan13(value) !== null;

const createStickerHTML = (product, quantity) => {
  const dimensions = product.dimensions;

  let dimensionsHtml = "";
  if (dimensions) {
    if (dimensions.height && dimensions.diameter) {
      dimensionsHtml = `
        <div class="info-row">
          <span class="label">HEIGHT:</span>
          <span class="value">${dimensions.height} cm</span>
        </div>
        <div class="info-row">
          <span class="label">DIAMETER:</span>
          <span class="value">${dimensions.diameter} cm</span>
        </div>
      `;
    } else if (dimensions.length && dimensions.width && dimensions.height) {
      dimensionsHtml = `
        <div class="info-row">
          <span class="label">LENGTH:</span>
          <span class="value">${dimensions.length} cm</span>
        </div>
        <div class="info-row">
          <span class="label">WIDTH:</span>
          <span class="value">${dimensions.width} cm</span>
        </div>
        <div class="info-row">
          <span class="label">HEIGHT:</span>
          <span class="value">${dimensions.height} cm</span>
        </div>
      `;
    } else if (dimensions.length && dimensions.width) {
      dimensionsHtml = `
        <div class="info-row">
          <span class="label">LENGTH:</span>
          <span class="value">${dimensions.length} cm</span>
        </div>
        <div class="info-row">
          <span class="label">WIDTH:</span>
          <span class="value">${dimensions.width} cm</span>
        </div>
      `;
    }
  }

  return `
    <div class="sticker" style="
      width: 550px;
      height: 367px;
      border: none;
      display: flex;
      flex-direction: column;
      background: white;
      font-family: 'Montserrat', sans-serif;
      box-sizing: border-box;
    ">
      <div class="header" style="
        padding: 24px ${INLINE_PADDING}px 12px;
      ">
        <div style="display: inline-block; position: relative;">
          <img src="/img/logo.webp" alt="BOMBYXX" style="
            height: 25px;
            display: block;
            margin-bottom: 3px;
          " />
          <div class="tagline" style="
            font-size: 9px;
            color: ${STICKER_PRIMARY};
            letter-spacing: 1.5px;
            font-weight: 300;
            text-align: right;
          ">NATURE INSPIRED</div>
        </div>
      </div>

      <div class="content" style="
        flex: 1;
        display: flex;
        padding: 18px ${INLINE_PADDING}px;
        gap: 20px;
      ">
        <div class="left-section" style="
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 14px;
        ">
          <div class="item-no" style="
            border-bottom: 1px solid #333;
            margin-bottom: 6px;
          ">
            <span class="label" style="
              font-size: 9px;
              font-weight: 500;
              color: #333;
              letter-spacing: 1px;
            ">ITEM NO.:</span>
            <span class="value" style="
              font-size: 11px;
              font-weight: 500;
              color: #333;
              margin-left: 8px;
            ">${product.itemNo}</span>
          </div>
          <div class="product-name" style="
            font-size: 12px;
            font-weight: 700;
            color: ${STICKER_PRIMARY};
            line-height: 1.2;
            margin-bottom: 8px;
          ">${(product.name || "").toUpperCase()}</div>
          ${
            product.colorName
              ? `
          <div class="info-row" style="
            display: flex;
            align-items: baseline;
            gap: 8px;
          ">
            <span class="label" style="
              font-size: 9px;
              font-weight: 400;
              color: #333;
              min-width: 75px;
              letter-spacing: 0.4px;
            ">COLOR:</span>
            <span class="value" style="
              font-size: 11px;
              color: #333;
              font-weight: 500;
            ">${product.colorName}</span>
          </div>
          `
              : ""
          }

          ${dimensionsHtml
            .replace(
              /class="info-row"/g,
              'class="info-row" style="display: flex; align-items: baseline; gap: 8px;"',
            )
            .replace(
              /class="label"/g,
              'class="label" style="font-size: 9px; font-weight: 400; color: #333; min-width: 75px; letter-spacing: 0.4px;"',
            )
            .replace(
              /class="value"/g,
              'class="value" style="font-size: 11px; color: #333; font-weight: 500;"',
            )}
          <div class="info-row" style="
            display: flex;
            align-items: baseline;
            gap: 8px;
          ">
            <span class="label" style="
              font-size: 9px;
              font-weight: 400;
              color: #333;
              min-width: 75px;
              letter-spacing: 0.4px;
            ">PCS:</span>
            <span class="value" style="
              font-size: 11px;
              color: #333;
              font-weight: 500;
            ">${quantity}</span>
          </div>
        </div>

        <div class="right-section" style="
          display: flex;
          align-items: flex-end;
          justify-content: flex-end;
          width: 70px;
          padding-bottom: 12px;
        ">
        </div>
      </div>

      <div class="footer" style="
        background: ${STICKER_FOOTER_BG};
        color: white;
        text-align: left;
        padding: 10px ${INLINE_PADDING}px;
        font-size: 8px;
        letter-spacing: 0.8px;
        font-weight: 500;
      ">
        DESIGN BY BOMBYXX, THE NETHERLANDS
      </div>
    </div>
  `;
};

/**
 * Productdocument → de velden die op een sticker staan.
 * `colorName` los meegeven: kleuren staan als id op het product.
 */
export const toStickerProduct = (product, colorName) => ({
  itemNo: product.code || product.id || "",
  name: product.name || "",
  barcode: product.barcode,
  colorName: colorName || undefined,
  dimensions: {
    length: product.length,
    width: product.width,
    height: product.height,
    diameter: product.diameter,
  },
});

/**
 * Rendert printopdrachten (`{ product, variant, quantity, copies }`) naar één
 * PDF en start de download.
 *
 * De afbeelding van een sticker wordt per opdracht één keer gemaakt en daarna
 * hergebruikt voor elke kopie — html2canvas is het dure deel, en zonder die
 * cache kost twintig kopieën twintig keer zoveel tijd.
 *
 * @returns {Promise<{ pages: number, failed: Array, withoutBarcode: Array }>}
 *   Wat er gemaakt is en wat er misging; de aanroeper bepaalt de melding.
 */
export const generateStickerPdf = async (jobs, fileName) => {
  const failed = [];
  const withoutBarcode = [];
  let container = null;
  let link = null;
  let pages = 0;

  const printable = (jobs || []).filter((job) => job.copies > 0 && job.quantity > 0);
  if (printable.length === 0) {
    return { pages: 0, failed, withoutBarcode };
  }

  try {
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: [100, 150],
    });

    container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-9999px";
    container.style.top = "0";
    document.body.appendChild(container);

    link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    await new Promise((resolve) => setTimeout(resolve, 100));

    for (const job of printable) {
      try {
        container.innerHTML = createStickerHTML(job.product, job.quantity);
        const stickerElement = container.querySelector(".sticker");

        const canvas = await html2canvas(stickerElement, {
          scale: 3,
          backgroundColor: "#ffffff",
          logging: false,
          useCORS: true,
        });
        const imgData = canvas.toDataURL("image/jpeg", 0.95);

        let barcodeData = null;
        const ean = normalizeEan13(job.product.barcode);
        if (ean) {
          const barcodeCanvas = document.createElement("canvas");
          JsBarcode(barcodeCanvas, ean, {
            format: "EAN13",
            width: 1,
            displayValue: true,
            fontSize: 24,
            margin: 1,
            fontOptions: "regular",
          });
          barcodeData = barcodeCanvas.toDataURL("image/png");
        } else if (!withoutBarcode.includes(job.product.name)) {
          withoutBarcode.push(job.product.name);
        }

        for (let copy = 0; copy < job.copies; copy++) {
          if (pages > 0) {
            pdf.addPage();
          }

          pdf.addImage(imgData, "JPEG", 0, 0, 150, 100);

          if (barcodeData) {
            const barcodeWidth = 30;
            const barcodeHeight = 22;
            pdf.addImage(
              barcodeData,
              "PNG",
              150 - barcodeWidth - 13,
              100 - barcodeHeight - 20,
              barcodeWidth,
              barcodeHeight,
            );
          }

          pages++;
        }
      } catch (jobError) {
        console.error(`Sticker voor ${job.product.name} mislukt:`, jobError);
        if (!failed.some((f) => f.name === job.product.name)) {
          failed.push({
            name: job.product.name,
            error: jobError instanceof Error ? jobError.message : String(jobError),
          });
        }
      }
    }

    if (pages > 0) {
      pdf.save(fileName);
    }

    return { pages, failed, withoutBarcode };
  } finally {
    if (container && document.body.contains(container)) {
      document.body.removeChild(container);
    }
    if (link && document.head.contains(link)) {
      document.head.removeChild(link);
    }
  }
};
